import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { HiOutlineSparkles } from "react-icons/hi2";
import {
  FiCloud, FiCode, FiTrendingUp, FiArrowLeft, FiArrowRight, FiPause, FiPlay,
} from "react-icons/fi";
import JCLocation from '../components/JCLocation';
import ToTop from '../components/ToTop';
import '../styles/home.css';

// Four head services. Each sub-item id matches a section id in Services.jsx,
// which is also what the navbar mega-menu links against.
const HOME_PILLARS = [
  {
    pillar: 'AI Solutions',
    icon: <HiOutlineSparkles />,
    featured: true,
    body: 'AI solutions that reduce manual effort, enhance productivity and create new business capabilities.',
    items: [
      { id: 'ai-agents', label: 'AI agents: voice, sales & support' },
      { id: 'generative-ai-development', label: 'Generative AI development' },
      { id: 'ai-automation', label: 'AI automation' },
      { id: 'ai-consulting', label: 'AI readiness & use-case roadmapping' },
    ],
  },
  {
    pillar: 'Cloud & DevOps',
    icon: <FiCloud />,
    body: 'Modernize, secure and optimize your cloud environment with engineering, automation and managed operations.',
    items: [
      { id: 'cloud-migration-modernization', label: 'Cloud migration & modernization' },
      { id: 'aws-managed-services', label: 'AWS managed services & FinOps' },
      { id: 'cloud-security-audits', label: 'Cloud security & best-practice audits' },
      { id: 'devops-platform-engineering', label: 'DevOps & platform engineering' },
    ],
  },
  {
    pillar: 'Software & Data Engineering',
    icon: <FiCode />,
    body: 'Cloud-native applications, APIs and data platforms built to scale: the products your customers touch and the systems your operations run on.',
    items: [
      { id: 'custom-software-web-applications', label: 'Custom web & mobile applications' },
      { id: 'backend-api-integration', label: 'Backend & API integration' },
      { id: 'data-engineering-analytics', label: 'Data engineering & analytics' },
    ],
  },
  {
    pillar: 'Technology Consulting',
    icon: <FiTrendingUp />,
    body: 'Architecture, security and scalability decisions framed against the business case, plus the enablement that leaves your team able to run what we build.',
    items: [
      { id: 'cloud-digital-transformation-strategy', label: 'Cloud & digital transformation strategy' },
      { id: 'cto-advisory-architecture-consulting', label: 'CTO advisory & architecture consulting' },
    ],
  },
];

// The engagement model, which is also the differentiator: the same team stays on
// the work from discovery through to handover, and keeps evolving it after.
const HOME_FLOW = ['Discover', 'Design', 'Deliver', 'Evolve'];

// One lap of the comet. Each stop lights for a quarter of it, hence the 2s
// stagger on the stop animations in home.css.
const ROUTE_LAP_MS = 8000;

/* Two drawings of the same route, one per shape of panel. CSS shows exactly one.

   Both are built from three curves that meet AT the stops with a shared tangent,
   so each stop sits in the middle of a smooth U-bend rather than on a corner. The
   three curves are congruent — each is the previous one mirrored — so they are
   the same arc length and the stops land on exact thirds of the path. That is
   what lets the comet reach each one on the 0s / 2s / 4s / 6s beat the stop
   animations use. Redraw the curves and keep them congruent, or the lights drift
   away from the dot.

   wide: stops alternate above and below the line, labels outside the bend, the
         way the reference infographic reads. Each curve is an S between a crest
         and a trough, flat at both ends.
   tall: for panels too narrow to hold four labels across. Each curve is a half-
         round U bulging out to alternate sides, labels to the right on a tie. */
const JOURNEY_VIEWS = [
  {
    key: 'wide',
    viewBox: '0 0 520 270',
    path:
      'M 70 80 C 140 80 120 200 190 200 C 260 200 240 80 310 80 C 380 80 360 200 430 200',
    stops: [
      { x: 70, y: 80, labelX: 70, labelY: 47, anchor: 'middle' },
      { x: 190, y: 200, labelX: 190, labelY: 244, anchor: 'middle' },
      { x: 310, y: 80, labelX: 310, labelY: 47, anchor: 'middle' },
      { x: 430, y: 200, labelX: 430, labelY: 244, anchor: 'middle' },
    ],
  },
  {
    key: 'tall',
    viewBox: '0 0 400 340',
    path:
      'M 90 34 C 160 34 160 124 90 124 C 20 124 20 214 90 214 C 160 214 160 304 90 304',
    stops: [
      { x: 90, y: 34, labelX: 210, labelY: 40, anchor: 'start', tie: [104, 196] },
      { x: 90, y: 124, labelX: 210, labelY: 130, anchor: 'start', tie: [104, 196] },
      { x: 90, y: 214, labelX: 210, labelY: 220, anchor: 'start', tie: [104, 196] },
      { x: 90, y: 304, labelX: 210, labelY: 310, anchor: 'start', tie: [104, 196] },
    ],
  },
];

// Whichever variant CSS hides is display:none, so it drops out of the
// accessibility tree on its own and only the visible one is announced.
function JourneyRoute({ view }) {
  return (
    <svg
      className={`home-journey home-journey-${view.key}`}
      viewBox={view.viewBox}
      role="img"
      aria-label="Our approach: discover, design, deliver, then evolve."
      style={{ '--home-journey-lap': `${ROUTE_LAP_MS}ms` }}
    >
      <path className="home-journey-base" d={view.path} pathLength="1000" />
      <path className="home-journey-comet" d={view.path} pathLength="1000" />

      {view.stops.map((stop, i) => (
        /* A custom property rather than animation-delay: delay does not inherit,
           but a custom property does, so the halo and the label below both pick
           up this stop's turn. */
        <g key={HOME_FLOW[i]} style={{ '--home-journey-turn': `${i * (ROUTE_LAP_MS / 4)}ms` }}>
          {stop.tie && (
            <line
              className="home-journey-tie"
              x1={stop.tie[0]}
              y1={stop.y}
              x2={stop.tie[1]}
              y2={stop.y}
            />
          )}
          <circle className="home-journey-halo" cx={stop.x} cy={stop.y} r="13" />
          <circle className="home-journey-core" cx={stop.x} cy={stop.y} r="5" />
          <text
            className="home-journey-label"
            x={stop.labelX}
            y={stop.labelY}
            textAnchor={stop.anchor}
          >
            {HOME_FLOW[i]}
          </text>
        </g>
      ))}
    </svg>
  );
}

const HOME_WHY = [
  {
    num: '01',
    title: 'Strategy and delivery in one team',
    body: 'The people who shape the strategy are the people who build it. No handoffs. No translation gaps. Just one team from strategy to production.',
  },
  {
    num: '02',
    title: 'Measured on business outcomes, not tickets closed',
    body: 'Every engagement starts with the outcome that matters, reducing cost, accelerating delivery, improving productivity, or creating new business value.',
  },
  {
    num: '03',
    title: 'We build your team, not your dependency',
    body: 'Knowledge transfer and enablement are in scope by default. Success looks like your engineers running it confidently without us.',
  },
  {
    num: '04',
    title: 'Built for global delivery',
    body: 'Our distributed team combines local client engagement with deep engineering capability, giving businesses access to high-quality expertise with the flexibility to scale.',
  },
];

// The three readiness questions. Each one is the opening question of an
// assessment, which is what the band below them offers.
const HOME_READINESS = [
  {
    title: 'AI Readiness',
    question: 'Can you turn AI from experimentation into measurable value?',
  },
  {
    title: 'Cloud Readiness',
    question: 'Can your cloud foundation scale securely and efficiently?',
  },
  {
    title: 'Engineering Readiness',
    question: 'Can your teams build, deploy and evolve at speed?',
  },
];

// Slides for the proof-of-work carousel. Everything here is already published on
// the AWS practice page, so nothing is new disclosure. Managed-services clients
// stay unnamed until a reference is signed off — Qride and Solopreneur are the
// project names already public on that page, not client names. Do not put a
// client name in `id` either: it ends up in the JS bundle.
const HOME_PROOF = [
  {
    id: 'sports-analytics',
    tag: 'Active · Multi-tenant AWS estate',
    name: 'Sports analytics platform',
    body: 'For an Africa-based sports-tech client, secure multi-tenant hosting keeps competing teams’ data isolated, with Transit Gateway network isolation and centralized, immutable logging. SageMaker models sit behind the predictive analytics.',
    metrics: [
      { k: 'Tenant isolation', l: 'Organizations, Control Tower and service control policies' },
      { k: 'Predictive analytics', l: 'SageMaker models for injury, form and valuation' },
      { k: 'Immutable logging', l: 'Centralized archives across every account' },
    ],
    link: 'See the architecture',
  },
  {
    id: 'qride',
    tag: 'Delivered · Mobility marketplace',
    name: 'Qride',
    body: 'A real-time ride-hailing marketplace coordinating a large driver network, designed, built and deployed on AWS with an enterprise security stack across WAF, Shield, GuardDuty and KMS.',
    metrics: [
      { k: 'Auto-scaling compute', l: 'Amazon ECS Fargate under live demand' },
      { k: 'Real-time tracking', l: 'IoT vehicle telemetry on the rider map' },
      { k: 'Marketplace safety', l: 'Driver KYC verification at onboarding' },
    ],
    link: 'See the architecture',
  },
  {
    id: 'solopreneur',
    tag: 'Case study · AI agents on Bedrock',
    name: 'Solopreneur',
    body: 'An AI-native workspace for small businesses that unifies CRM, calendar, email, meetings and tasks behind a single conversational interface, with the AI drafting actions rather than firing them off.',
    metrics: [
      { k: 'Agent orchestration', l: 'An AgentCore supervisor on Amazon Bedrock' },
      { k: 'Human in the loop', l: 'Approval step before anything is sent' },
      { k: 'One workspace', l: 'Specialized agents behind one interface' },
    ],
    link: 'See the architecture',
  },
  {
    id: 'managed-estate',
    tag: 'Active · AWS managed services',
    name: 'Managed AWS estate',
    body: 'An end-to-end managed service on AWS: monitoring, maintenance, performance optimization, cost management and cloud governance, so the client’s team ships product instead of babysitting infrastructure.',
    metrics: [
      { k: 'Continuous monitoring', l: 'Proactive alerting and incident response' },
      { k: 'Cost governance', l: 'Ongoing right-sizing of the estate' },
      { k: 'Centralized control', l: 'One governance model across accounts' },
    ],
  },
];

// How long each engagement holds the frame before the carousel moves on.
const PROOF_INTERVAL_MS = 6000;

function Home() {
  const [proofSlide, setProofSlide] = useState(0);

  // Two ways the carousel can be held. proofPaused is transient — hovering,
  // focusing or touching it — and releases itself. proofStopped is the reader
  // pressing pause, and stays until they press play, so moving the mouse away
  // does not start it moving again under them.
  const [proofPaused, setProofPaused] = useState(false);
  const [proofStopped, setProofStopped] = useState(false);
  const proofHeld = proofPaused || proofStopped;

  // Swipe start, kept in a ref so a drag does not re-render on every move.
  const touchStartX = useRef(null);

  // The navbar sits outside this component, so route-specific navbar styling
  // has to be flagged on the body rather than scoped under the wrapper.
  useEffect(() => {
    document.body.classList.add('home-route');
    return () => document.body.classList.remove('home-route');
  }, []);

  const goToProof = (index) => {
    const count = HOME_PROOF.length;
    setProofSlide(((index % count) + count) % count);
  };

  // Relative moves read the current slide from the updater rather than the
  // render closure, so two advances landing in one batch still move two.
  const stepProof = useCallback((delta) => {
    const count = HOME_PROOF.length;
    setProofSlide((current) => (((current + delta) % count) + count) % count);
  }, []);

  // Auto-advance. proofSlide is a dependency so the timer restarts whenever the
  // slide changes: after a manual jump the new slide gets a full dwell rather
  // than whatever was left on the clock.
  useEffect(() => {
    if (proofHeld) return undefined;
    // Anyone who has asked their OS to reduce motion gets a static carousel.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const timer = setInterval(() => stepProof(1), PROOF_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [proofHeld, proofSlide, stepProof]);

  const onProofKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      stepProof(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      stepProof(1);
    }
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setProofPaused(true);
  };

  // 45px is far enough to rule out a tap but short enough for a thumb flick.
  const onTouchEnd = (e) => {
    if (touchStartX.current !== null) {
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(delta) > 45) stepProof(delta < 0 ? 1 : -1);
      touchStartX.current = null;
    }
    setProofPaused(false);
  };

  return (
    <div className="home-page">
      {/* Hero */}
      <header className="home-hero">
        <div className="home-hero-grid" aria-hidden="true"></div>
        <div className="home-wrap home-hero-inner">
          <div>
            <span className="home-eyebrow">Strategy &middot; Cloud &middot; AI &middot; Engineering</span>
            <h1>
              We turn cloud and AI investments into{' '}
              <em className="home-hero-accent">measurable business value</em>.
            </h1>
            <p className="home-hero-lead">
              Jaggulas Consulting brings strategy, cloud, AI and engineering together to transform
              business challenges into scalable solutions.
            </p>
            <div className="home-hero-ctas">
              <Link to="/Contact" className="home-btn home-btn-primary">
                Book a Strategy Session &rarr;
              </Link>
              <HashLink to="/Services#pillars" className="home-btn home-btn-ghost">
                Explore our capabilities
              </HashLink>
            </div>
          </div>

          <div className="home-hero-visual">
            <div className="home-orb" aria-hidden="true"></div>
            <div className="home-flow">
              <p className="home-flow-title">Our approach</p>
              {JOURNEY_VIEWS.map((view) => (
                <JourneyRoute key={view.key} view={view} />
              ))}
            </div>
          </div>
        </div>
      </header>
      {/* Hero ends */}

      {/* Four practices */}
      <section className="home-band" id="practices">
        <div className="home-wrap">
          <div className="home-sec-head">
            <div>
              <span className="home-eyebrow">What we do</span>
              <h2>Four practices, one delivery team</h2>
            </div>
          </div>

          <div className="home-pillars">
            {HOME_PILLARS.map((group) => (
              <div
                className={'home-pillar' + (group.featured ? ' home-pillar-feat' : '')}
                key={group.pillar}
              >
                <div className="home-pillar-icon" aria-hidden="true">{group.icon}</div>
                <h3 className="card-heading">{group.pillar}</h3>
                <p className="home-pillar-body">{group.body}</p>
                <ul className="home-pillar-subs">
                  {group.items.map((svc) => (
                    <li key={svc.id}>
                      <HashLink to={`/Services#${svc.id}`} className="home-pillar-sub">
                        {svc.label}
                      </HashLink>
                    </li>
                  ))}
                </ul>
                <HashLink to={`/Services#${group.items[0].id}`} className="home-pillar-link">
                  Explore {group.pillar} &rarr;
                </HashLink>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Four practices ends */}

      {/* Proof of work */}
      <section className="home-band home-band-alt">
        <div className="home-wrap">
          <div className="home-sec-head">
            <div>
              <span className="home-eyebrow">Proof of work</span>
              <h2>From Vision to Value</h2>
            </div>
            <p className="home-sec-lead">
              Explore how we turn complex business challenges into intelligent, scalable solutions.
            </p>
          </div>

          {/*
            The managed-services client has agreed to provide a public reference
            but it is not signed off yet. Once it is, that slide can carry the
            client name and the approved quote, under the metrics row:

            <blockquote className="home-case-quote">
              <p>Approved quote.</p>
              <footer>Name, Role, Company</footer>
            </blockquote>
          */}
          {/* Auto-advancing, but it holds still while anyone is reading it:
              hovering, focusing a control or touching the cards all pause it. */}
          <div
            className="home-proof"
            role="group"
            aria-roledescription="carousel"
            aria-label="Proof of work"
            onKeyDown={onProofKeyDown}
            onMouseEnter={() => setProofPaused(true)}
            onMouseLeave={() => setProofPaused(false)}
            onFocus={() => setProofPaused(true)}
            onBlur={() => setProofPaused(false)}
          >
            <div
              className="home-proof-viewport"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {/* All slides stay in flow, so the track is as tall as the tallest
                  card and the section does not jump height between slides. */}
              <div
                className="home-proof-track"
                style={{ transform: `translateX(-${proofSlide * 100}%)` }}
              >
                {HOME_PROOF.map((item, i) => (
                  <div
                    className="home-proof-slide"
                    key={item.id}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${item.name}, ${i + 1} of ${HOME_PROOF.length}`}
                    aria-hidden={i !== proofSlide}
                  >
                    <article className="home-case">
                      <div className="home-case-top">
                        <span className="home-tag">{item.tag}</span>
                        <h3>{item.name}</h3>
                        <p>{item.body}</p>
                        {item.link && (
                          <Link
                            to="/AWS"
                            className="home-case-link"
                            /* Off-screen slides must stay out of the tab order. */
                            tabIndex={i === proofSlide ? 0 : -1}
                          >
                            {item.link} &rarr;
                          </Link>
                        )}
                      </div>
                      <div className="home-case-metrics">
                        {item.metrics.map((m) => (
                          <div className="home-metric" key={m.k}>
                            <p className="home-metric-k">{m.k}</p>
                            <p className="home-metric-l">{m.l}</p>
                          </div>
                        ))}
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>

            <div className="home-proof-controls">
              <div
                className={'home-proof-dots' + (proofHeld ? ' is-paused' : '')}
                style={{ '--home-proof-interval': `${PROOF_INTERVAL_MS}ms` }}
              >
                {HOME_PROOF.map((item, i) => (
                  <button
                    type="button"
                    key={item.id}
                    className={'home-proof-dot' + (i === proofSlide ? ' is-active' : '')}
                    aria-label={`Show ${item.name}`}
                    aria-current={i === proofSlide}
                    onClick={() => goToProof(i)}
                  />
                ))}
              </div>
              {/* The toggle sits outside .home-proof-arrows on purpose: the arrows
                  are hidden on narrow screens in favour of swiping, but pause has
                  to stay — a touch reader has no hover to hold it with. */}
              <div className="home-proof-actions">
                <button
                  type="button"
                  className="home-proof-arrow home-proof-toggle"
                  aria-label={
                    proofStopped
                      ? 'Play the engagement carousel'
                      : 'Pause the engagement carousel'
                  }
                  onClick={() => setProofStopped((stopped) => !stopped)}
                >
                  {proofStopped ? <FiPlay /> : <FiPause />}
                </button>

                <div className="home-proof-arrows">
                  <button
                    type="button"
                    className="home-proof-arrow"
                    aria-label="Previous engagement"
                    onClick={() => stepProof(-1)}
                  >
                    <FiArrowLeft />
                  </button>
                  <button
                    type="button"
                    className="home-proof-arrow"
                    aria-label="Next engagement"
                    onClick={() => stepProof(1)}
                  >
                    <FiArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Proof of work ends */}

      {/* Why Jaggulas */}
      <section className="home-band">
        <div className="home-wrap home-why">
          <div className="home-why-head">
            <span className="home-eyebrow">Why Jaggulas</span>
            <h2>Strategists who build. Engineers who think.</h2>
          </div>
          <ul className="home-why-list">
            {HOME_WHY.map((item) => (
              <li className="home-why-item" key={item.num}>
                <span className="home-why-num" aria-hidden="true">{item.num}</span>
                <div>
                  <h3 className="card-heading">{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
      {/* Why Jaggulas ends */}

      {/* Technology readiness */}
      <section className="home-band home-band-alt">
        <div className="home-wrap">
          <div className="home-readiness">
            <span className="home-eyebrow">Technology Readiness</span>
            <h2>Are you ready for what&rsquo;s next?</h2>

            <div className="home-ready-grid">
              {HOME_READINESS.map((item) => (
                <div className="home-ready" key={item.title}>
                  <h3 className="card-heading">{item.title}</h3>
                  <p>{item.question}</p>
                </div>
              ))}
            </div>

            <p className="home-ready-close">
              We assess your current state, identify the gaps, and build the roadmap to move
              forward.
            </p>

            <Link to="/Contact" className="home-btn home-btn-primary home-ready-btn">
              Assess Your Readiness &rarr;
            </Link>
          </div>
        </div>
      </section>
      {/* Technology readiness ends */}

      {/* Location */}
      <JCLocation />
      {/* Location ends */}

      {/* To top icon */}
      <ToTop />
      {/* To top icon */}
    </div>
  )
}

export default Home
